/**
 * WISCHEN IN DER BILDVORSCHAU.
 *
 * ORIGINALTEXT MARVIN (15.09.2026, Bild 6)
 * > „bei der vorschau von bildern, da hast du oben zwar die buttons, aber man
 * >  ist ja «wishen» auf dem smartphone gewohnt. baue das ein mit zwei
 * >  leichten doppelpfeilen im bild damit man weiß das man wischen kann."
 *
 * Die Knöpfe über dem Bild gibt es seit .130 und sie bleiben: **der Zeiger am
 * Schreibtisch wischt nicht.** Was dazukommt, ist die Handbewegung, die auf
 * einem Telefon jeder erwartet - und der HINWEIS darauf, denn eine Geste, die
 * man nicht sieht, ist von einer fehlenden nicht zu unterscheiden.
 *
 * ## DIE REGELN STEHEN HIER UND NICHT IM BAUTEIL
 *
 * DIESE DATEI TRÄGT KEIN JSX, damit ein Prüfstand sie WIRKLICH ausrechnen
 * kann. Wohin ein Zug von 60 Punkten nach links bei 40 Punkten nach unten
 * führt, lässt sich nicht suchen, das muss man nachrechnen - **eine Textsuche
 * misst, dass etwas dasteht, nicht, dass es stimmt.** Dieselbe Aufteilung wie
 * bei `terminraster.ts`, `kartenebene.ts`, `grenzpunkte.ts` und den übrigen
 * Regeldateien in `web/src/teile/`; **hier steht keine Zahl daneben, wie
 * viele es sind** - eine abgezählte Zahl in einem Kommentar ist eine
 * Standnummer, und sie stimmt ab der nächsten solchen Datei nicht mehr.
 *
 * ## WARUM ES KEIN `preventDefault` GIBT
 *
 * Der naheliegende Bau wäre `onTouchMove` mit `preventDefault()`, und er ist
 * der falsche: **React hängt `touchstart` und `touchmove` PASSIV am
 * Wurzelknoten an, und ein passiver Zuhörer darf nicht abbrechen** - der Ruf
 * wird stillschweigend übergangen, ohne Fehler und ohne Meldung in der
 * Konsole. Dieses Haus hat das am 02.09.2026 an der Tiefbaukarte bezahlt.
 *
 * Gebraucht wird es hier auch gar nicht. `touch-action: pan-y` am Bild sagt
 * dem Browser: **waagerecht gehört die Geste uns, senkrecht bleibt das
 * Rollen** - und das ist eine Zusage im Stylesheet und kein Ruf, den jemand
 * übergehen kann. Die Zeiger-Ereignisse (`pointerdown`/`up`) hängt React
 * NICHT passiv an; sie tragen den Rest.
 *
 * ## WAS EIN WISCH IST UND WAS EIN VERSEHEN
 *
 * Drei Riegel, und jeder hat seinen Grund:
 *
 *  1. **Eine Mindeststrecke.** Ein Zug von 10 Punkten ist ein Tippen, das
 *     verrutscht ist. Wer daraus ein Blättern macht, wechselt das Bild, wenn
 *     jemand es nur anfassen wollte.
 *  2. **Waagerecht muss deutlich gewinnen.** Ein Zug, der ebenso weit nach
 *     unten geht, ist ein Rollversuch - und das Bild steht in einem Fenster,
 *     das rollt. Ohne diesen Riegel blättert die Vorschau, sobald jemand das
 *     Fenster bewegt.
 *  3. **Nur EIN Zeiger.** Zwei Finger sind ein Zoomversuch. Wer den zweiten
 *     mitzählt, blättert mitten im Vergrössern - und genau dann sieht jemand
 *     gerade sehr genau hin.
 *
 * DIE RICHTUNG FOLGT DEM INHALT UND NICHT DEM KNOPF. Wer nach LINKS zieht,
 * schiebt das Bild nach links aus dem Fenster, und von rechts kommt das
 * NÄCHSTE nach - so hält es jede Bildergalerie, die jemand schon einmal
 * bedient hat. Andersherum wäre es eine Regel, die man lernen muss.
 */

/** Wohin ein Wisch führt. Dieselben 2 Namen wie an den Knöpfen darüber. */
export type Richtung = 'vor' | 'zurueck';

/**
 * Wie weit ein Finger mindestens ziehen muss, in CSS-Punkten.
 *
 * 48 und nicht 10: ein Daumen trifft nichts unter 44 Punkten genau (dieselbe
 * Zahl wie an jedem Knopf dieses Hauses), und wer ein Bild anfasst, verrutscht
 * dabei um ein paar Punkte.
 */
export const MINDESTSTRECKE = 48;

/**
 * Um wie viel die waagerechte Strecke die senkrechte schlagen muss.
 *
 * Bei 1 wäre jede Diagonale ein Wisch, und eine Rollbewegung geht selten
 * genau senkrecht. 1,5 heisst: der Zug muss erkennbar zur Seite gehen.
 */
export const VERHAELTNIS = 1.5;

/** Was ein begonnener Zug mit sich trägt. */
export type Wischstand = {
  /** Der Zeiger, der begonnen hat. Ein zweiter bricht ab. */
  zeigerId: number;
  x: number;
  y: number;
  /** Ein zweiter Finger ist ein Zoomversuch und kein Wisch. */
  abgebrochen: boolean;
};

/** Ein Zug fängt an. */
export function wischStart(zeigerId: number, x: number, y: number): Wischstand {
  return { zeigerId, x, y, abgebrochen: false };
}

/**
 * Ein zweiter Zeiger kommt dazu.
 *
 * ABGEBROCHEN WIRD UND NICHT UMGESCHALTET. Wer den neuen Zeiger übernähme,
 * bekäme beim Loslassen des ersten Fingers eine Strecke zwischen zwei
 * verschiedenen Fingern - das ist keine Handbewegung, das ist eine Rechnung
 * ohne Gegenstand.
 */
export function wischZweiterZeiger(stand: Wischstand | null): Wischstand | null {
  if (!stand) return null;
  return { ...stand, abgebrochen: true };
}

/**
 * Aus einer Strecke die Richtung - oder nichts.
 *
 * Das ist die ganze Entscheidung; alles darüber und darunter ist Buchführung.
 */
export function wischrichtung(dx: number, dy: number): Richtung | null {
  const waagerecht = Math.abs(dx);
  const senkrecht = Math.abs(dy);
  if (!Number.isFinite(waagerecht) || !Number.isFinite(senkrecht)) return null;
  if (waagerecht < MINDESTSTRECKE) return null;
  if (waagerecht < senkrecht * VERHAELTNIS) return null;
  /* Nach links gezogen heisst: das Bild geht nach links hinaus, und von
     rechts kommt das nächste nach. */
  return dx < 0 ? 'vor' : 'zurueck';
}

/**
 * Der Finger geht hoch. Gibt die Richtung zurück, oder `null`, wenn es kein
 * Wisch war.
 *
 * DER ZEIGER MUSS DERSELBE SEIN. Auf einem Gerät mit Stift und Finger kommen
 * sonst zwei Ereignisse durcheinander, und die Strecke wäre zwischen zwei
 * verschiedenen Geräten gemessen.
 */
export function wischEnde(
  stand: Wischstand | null, zeigerId: number, x: number, y: number,
): Richtung | null {
  if (!stand || stand.abgebrochen) return null;
  if (stand.zeigerId !== zeigerId) return null;
  return wischrichtung(x - stand.x, y - stand.y);
}

/**
 * WELCHE PFEILE INS BILD GEHÖREN.
 *
 * **EIN PFEIL, DER INS LEERE ZEIGT, IST SCHLIMMER ALS KEIN PFEIL.** Er sagt
 * „hier geht es weiter", und wer ihm folgt, hält das System für kaputt. Sie
 * stehen deshalb genau dort, wo es wirklich eine nächste Datei gibt - bei der
 * ersten also nur rechts, bei der letzten nur links.
 *
 * Bei einer einzelnen Datei gibt es gar kein Blättern; dann steht auch kein
 * Hinweis darauf da.
 */
export function pfeile(blaettern?: { zurueck?: unknown; vor?: unknown } | null): {
  zurueck: boolean; vor: boolean;
} {
  if (!blaettern) return { zurueck: false, vor: false };
  return { zurueck: !!blaettern.zurueck, vor: !!blaettern.vor };
}
